import { motion } from "framer-motion";
import { flow, type FlowStage } from "@/data/showcase";
import { fadeUp, staggerSlow, viewportOnce } from "@/lib/motion";

const { eyebrow, titleStart, titleItalic, stages } = flow;

export function Flow() {
  return (
    <section className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden">
      {/* Subtle vertical purple band on the right, schematic-feel */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: 0,
          right: 0,
          width: "40%",
          height: "100%",
          background:
            "radial-gradient(ellipse at 80% 50%, rgba(141,81,255,0.06), transparent 60%)",
        }}
      />
      {/* Subtle particle stream — kept, but reduced to 6 dots */}
      <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
        {Array.from({ length: 6 }, (_, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              top: `${48 + ((i * 6) % 12) - 6}%`,
              left: "0%",
              width: "3px",
              height: "3px",
              background: "#BB97FF",
              boxShadow: "0 0 6px #8D51FF",
              animation: "prob-particle-fly 8s ease-in-out infinite",
              animationDelay: `${i * 1.2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1080px] px-6">
        <motion.div
          className="mb-12 md:mb-16 max-w-[760px]"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-ink-500 font-medium">
            {eyebrow}
          </p>
          <h2 className="font-serif text-white leading-[1.05] mt-4 text-3xl md:text-5xl lg:text-6xl max-w-[20ch]">
            {titleStart}{" "}
            <span className="text-foton-lighter italic font-display">{titleItalic}</span>
          </h2>
        </motion.div>

        {/* Schematic blueprint: 4 stages as numbered editorial entries with hairline rules */}
        <motion.ol
          className="relative"
          variants={staggerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {stages.map((s, i) => (
            <BlueprintStage key={s.n} stage={s} isLast={i === stages.length - 1} />
          ))}
        </motion.ol>
      </div>
    </section>
  );
}

function BlueprintStage({ stage, isLast }: { stage: FlowStage; isLast: boolean }) {
  const { n, name, sub, primary } = stage;
  return (
    <motion.li
      variants={fadeUp}
      className={"grid grid-cols-[80px_1fr] md:grid-cols-[120px_1fr] gap-4 md:gap-8 py-6 md:py-8 " + (isLast ? "" : "border-b border-ink-800")}
    >
      <div>
        <p
          className={
            "font-serif num leading-none text-4xl md:text-6xl " +
            (primary ? "text-foton-lighter" : "text-ink-700")
          }
        >
          {n}
        </p>
      </div>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <h3
            className={
              "font-serif text-xl md:text-2xl leading-tight " +
              (primary ? "text-white" : "text-ink-200")
            }
          >
            {name}
          </h3>
          {primary && (
            <span
              aria-hidden
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-foton-lighter shrink-0"
            >
              ✦ AI
            </span>
          )}
        </div>
        <p
          className={
            "font-mono text-[11px] uppercase tracking-[0.15em] " +
            (primary ? "text-foton-lighter/80" : "text-ink-500")
          }
        >
          {sub}
        </p>
      </div>
    </motion.li>
  );
}
